import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Autocomplete
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import UsersAutoComplete from "../../Users/UsersAutoComplete";

const FilterComponent = ({ setFilters }) => {
    const [checkedFields, setCheckedFields] = useState([
        { fieldName: "accountName", label: "Account Name", checked: false },
        { fieldName: "phone", label: "Phone", checked: false },
		{ fieldName: "website", label: "Website", checked: false },
        { fieldName: "ownerId", label: "Account Owner", checked: false },
        { fieldName: "createdBy", label: "Created By", checked: false },
        { fieldName: "createdAt", label: "Created Time", checked: false },
        { fieldName: "updatedBy", label: "Modified By", checked: false },
    ]);

    const [filterValues, setFilterValues] = useState({});
    const [filterOperators, setFilterOperators] = useState({});

    const handleCheckboxChange = (fieldName) => {
        setCheckedFields((prev) =>
            prev.map((field) =>
                field.fieldName === fieldName
                    ? { ...field, checked: !field.checked }
                    : field
            )
        );

        if (!filterValues[fieldName]) {
            setFilterValues({ ...filterValues, [fieldName]: "" });
            if (["createdAt"].includes(fieldName)) {
                setFilterOperators({ ...filterOperators, [fieldName]: 14 }); // operator "between" for dates
            }
            else {
                setFilterOperators({ ...filterOperators, [fieldName]: 2 }); // Default operator (e.g., "contains")
            }
        } else {
            const updatedValues = { ...filterValues };
            const updatedOperators = { ...filterOperators };
            delete updatedValues[fieldName];
            delete updatedOperators[fieldName];
            setFilterValues(updatedValues);
            setFilterOperators(updatedOperators);
        }
    };

    const handleFilterChange = (fieldName, value) => {
        // for dates we need the format "startDate:endData"
        if (["createdAt"].includes(fieldName)) {
            const { type, value: dateValue } = value; // Destructure here to avoid `type` not being defined
            setFilterValues((prevValues) => {
                const currentDates = prevValues[fieldName]?.split(';') || [null, null];
                const updatedDates = type === 'from'
                    ? [dateValue?.format('YYYY-MM-DD') || '', currentDates[1] || '']
                    : [currentDates[0] || '', dateValue?.format('YYYY-MM-DD') || ''];
                return {
                    ...prevValues,
                    [fieldName]: updatedDates.join(';'),
                };
            });
        }
        else {
            setFilterValues({ ...filterValues, [fieldName]: value });
        }
    };

    const handleOperatorChange = (fieldName, operator) => {
        setFilterOperators({ ...filterOperators, [fieldName]: operator });
    };

    const handleApplyFilter = () => {
        const getFieldType = (fieldName) => {
            // field type needed in backend
            const intFields = [];
            const dateFields = ["createdAt"];
            const relationalFields = [""];

            if (intFields.includes(fieldName)) return 0;
            if (dateFields.includes(fieldName)) return 2;
            if (relationalFields.includes(fieldName)) return 3;

            return 1; // 0 = int, 1 = string
        };

        const filterParams = checkedFields
            .filter((field) => field.checked)
            .map((field) => ({

                fieldName: field.fieldName,
                filterOperator: filterOperators[field.fieldName],
                fieldValue: filterValues[field.fieldName],
                fieldType: getFieldType(field.fieldName) // Assign correct field type dynamically
            }));

        setFilters(filterParams);
    };

    const handleClear = () => {
        setCheckedFields((prev) => prev.map((field) => ({ ...field, checked: false })));
        setFilterValues({});
        setFilterOperators({});
        setFilters([]);
    };

    return (
        <Box p={2} border="1px solid #ccc" borderRadius="8px" sx={{
            height: '100%', // Ensures the component takes the full height of the parent
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box'
          }}>
            <Typography variant="subtitle1" gutterBottom>
                Filter By Fields
            </Typography>
            <Box display="flex" flexDirection="column" gap={1} height="100%" overflow="auto">
                {checkedFields.map(({ fieldName, label, checked }) => (
                    <Box key={fieldName}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={checked}
                                    onChange={() => handleCheckboxChange(fieldName)}
                                />
                            }
                            label={label}
                        />
                        {checked && ["accountName", "phone", "website"].includes(fieldName) && (
                            <Box display="flex" flexDirection="column" mt={1} mr={1}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        value={filterOperators[fieldName] || 0}
                                        size="small"
                                        onChange={(e) =>
                                            handleOperatorChange(fieldName, e.target.value)
                                        }
                                    >
                                        <MenuItem value={0}>Equals</MenuItem>
                                        <MenuItem value={2}>Contains</MenuItem>
                                        <MenuItem value={4}>Starts With</MenuItem>
                                        <MenuItem value={5}>Ends With</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    label="Type here"
                                    size="small"
                                    value={filterValues[fieldName] || ""}
                                    onChange={(e) =>
                                        handleFilterChange(fieldName, e.target.value)
                                    }
                                />
                            </Box>
                        )}
                        {checked && ["createdAt"].includes(fieldName) && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="From Date"
                                        fullWidth
                                        required
                                        onChange={(newDate) => handleFilterChange(fieldName, { type: 'from', value: newDate })}
                                        slotProps={{
                                            textField: {
                                                variant: 'outlined',
                                                fullWidth: true,
                                                size: 'small'
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="To Date"
                                        fullWidth
                                        required
                                        onChange={(newDate) => handleFilterChange(fieldName, { type: 'to', value: newDate })}
                                        slotProps={{
                                            textField: {
                                                variant: 'outlined',
                                                fullWidth: true,
                                                size: 'small'
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                        )

                        }
                        {checked && ["createdBy", "updatedBy", "ownerId"].includes(fieldName) && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <UsersAutoComplete
                                    userId={filterValues[fieldName]} // Pass the current filter value for the field
                                    setUserId={(value) => handleFilterChange(fieldName, value)} // Call handleFilterChange with the field and selected value
                                    small={true}
                                />
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
            <Box mt={2} display="flex" justifyContent="space-between">
                <Button variant="contained" color="primary" onClick={handleApplyFilter}>
                    Apply Filter
                </Button>
                <Button variant="outlined" onClick={handleClear}>
                    Clear
                </Button>
            </Box>
        </Box>
    );
};

export default FilterComponent;
